import { useEffect, useState } from "react";
import { api, formatError, formatDateOnly, formatDate } from "../lib/api";
import { PageHeader, Modal, StatusBadge } from "../components/ui-kit";
import { Plus, Pencil, Trash2, Undo2, Printer } from "lucide-react";
import SignaturePad from "../components/SignaturePad";
import PhotoUploader from "../components/PhotoUploader";
import { openReceiptPrint } from "../lib/receipt";

const EMPTY = { plate: "", model: "", brand: "", status: "ativo", km: 0 };

export default function FleetPage() {
  const [vehicles, setVehicles] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [techs, setTechs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");

  const [aOpen, setAOpen] = useState(false);
  const [aForm, setAForm] = useState({ vehicle_id: "", user_id: "", start_date: new Date().toISOString().slice(0, 10), start_km: 0, signature: null, photos: [] });
  const [aError, setAError] = useState("");

  const [closeOpen, setCloseOpen] = useState(false);
  const [closeForm, setCloseForm] = useState({ vehicle: null, end_km: 0, signature: null, photos: [], notes: "" });
  const [closeErr, setCloseErr] = useState("");

  const load = () => {
    api.get("/vehicles").then((r) => setVehicles(r.data));
    api.get("/vehicle-assignments").then((r) => setAssignments(r.data));
    api.get("/users").then((r) => setTechs(r.data));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setError("");
    try {
      const payload = { ...form, km: Number(form.km) || 0 };
      if (editing) await api.put(`/vehicles/${editing}`, payload);
      else await api.post("/vehicles", payload);
      setOpen(false); load();
    } catch (e) { setError(formatError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este veículo?")) return;
    await api.delete(`/vehicles/${id}`);
    load();
  };

  const saveAssignment = async () => {
    setAError("");
    if (!aForm.signature) { setAError("A assinatura do funcionário é obrigatória"); return; }
    try {
      const payload = { ...aForm, start_km: aForm.start_km ? Number(aForm.start_km) : undefined };
      await api.post("/vehicle-assignments", payload);
      setAOpen(false);
      setAForm({ vehicle_id: "", user_id: "", start_date: new Date().toISOString().slice(0, 10), start_km: 0, signature: null, photos: [] });
      load();
    } catch (e) { setAError(formatError(e)); }
  };

  const openClose = (v) => {
    setCloseForm({ vehicle: v, end_km: v.km || 0, signature: null, photos: [], notes: "" });
    setCloseErr(""); setCloseOpen(true);
  };
  const doClose = async () => {
    setCloseErr("");
    if (!closeForm.signature) { setCloseErr("A assinatura de devolução é obrigatória"); return; }
    try {
      await api.post(`/vehicle-assignments/${closeForm.vehicle.current_assignment_id}/close`, {
        end_km: Number(closeForm.end_km),
        signature: closeForm.signature,
        photos: closeForm.photos,
        notes: closeForm.notes,
      });
      setCloseOpen(false); load();
    } catch (e) { setCloseErr(formatError(e)); }
  };

  const printVehicleReceipt = (a) => {
    openReceiptPrint({
      title: a.status === "encerrado" ? "Recibo de Devolução · Veículo" : "Recibo de Retirada · Veículo",
      subtitle: `${a.vehicle_plate}`,
      item: a.vehicle_plate,
      sn: null,
      technician: a.user_name,
      date: formatDate(a.start_date),
      signature: a.status === "encerrado" ? (a.return_signature || a.signature) : a.signature,
      photos: a.status === "encerrado" ? (a.return_photos || a.photos || []) : (a.photos || []),
      meta: [
        { label: "KM Inicial", value: a.start_km?.toLocaleString?.("pt-BR") ?? "-" },
        { label: "KM Final", value: a.end_km?.toLocaleString?.("pt-BR") ?? "-" },
        { label: "Início", value: formatDate(a.start_date) },
        { label: "Fim", value: a.end_date ? formatDate(a.end_date) : "Em aberto" },
        { label: "Observação Devolução", value: a.return_notes || "-" },
      ],
    });
  };

  return (
    <div data-testid="fleet-page">
      <PageHeader
        eyebrow="Frota"
        title="Veículos & Atribuições"
        actions={
          <>
            <button className="btn btn-secondary" onClick={() => { setAError(""); setAOpen(true); }} data-testid="new-assignment-btn">Atribuir Veículo</button>
            <button className="btn btn-primary" onClick={() => { setEditing(null); setForm(EMPTY); setError(""); setOpen(true); }} data-testid="new-vehicle-btn">
              <Plus size={16} /> Novo Veículo
            </button>
          </>
        }
      >
        Este módulo é visível apenas para gestores e administradores.
      </PageHeader>

      <div className="card mb-6">
        <div className="px-5 py-4 border-b" style={{ borderColor: "var(--border)" }}><h3 className="label-caps">Veículos</h3></div>
        <table className="data">
          <thead><tr><th>Placa</th><th>Modelo</th><th>Status</th><th>KM</th><th>Com técnico</th><th>Desde</th><th>Ações</th></tr></thead>
          <tbody>
            {vehicles.map((v) => (
              <tr key={v.id} data-testid={`vehicle-row-${v.plate}`}>
                <td className="mono">{v.plate}</td>
                <td>{v.model} <span className="text-xs" style={{ color: "var(--text-mute)" }}>({v.brand})</span></td>
                <td><StatusBadge status={v.status} /></td>
                <td className="mono">{v.km?.toLocaleString?.("pt-BR") ?? v.km}</td>
                <td>
                  {v.current_holder_name ? (
                    <span data-testid={`vehicle-holder-${v.plate}`}>{v.current_holder_name}</span>
                  ) : (
                    <span className="text-xs" style={{ color: "var(--text-mute)" }}>
                      {v.last_holder_name ? `Último: ${v.last_holder_name}` : "-"}
                    </span>
                  )}
                </td>
                <td className="text-xs" style={{ color: "var(--text-dim)" }}>{v.current_since ? formatDateOnly(v.current_since) : "-"}</td>
                <td>
                  <div className="flex gap-1">
                    {v.current_assignment_id && (
                      <button className="btn btn-ghost" onClick={() => openClose(v)} data-testid={`close-assignment-${v.plate}`} title="Encerrar uso e registrar KM">
                        <Undo2 size={13} /> Encerrar
                      </button>
                    )}
                    <button className="btn btn-ghost" onClick={() => { setEditing(v.id); setForm({ ...EMPTY, ...v }); setError(""); setOpen(true); }}><Pencil size={14} /></button>
                    <button className="btn btn-ghost" onClick={() => remove(v.id)}><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {vehicles.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-sm" style={{ color: "var(--text-mute)" }}>Nenhum veículo cadastrado.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="card">
        <div className="px-5 py-4 border-b" style={{ borderColor: "var(--border)" }}><h3 className="label-caps">Histórico de Atribuições</h3></div>
        <table className="data">
          <thead><tr><th>Veículo</th><th>Funcionário</th><th>Início</th><th>Fim</th><th>KM Inicial</th><th>KM Final</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {assignments.map((a) => (
              <tr key={a.id}>
                <td className="mono">{a.vehicle_plate}</td>
                <td>{a.user_name}</td>
                <td>{formatDateOnly(a.start_date)}</td>
                <td>{a.end_date ? formatDateOnly(a.end_date) : "-"}</td>
                <td className="mono">{a.start_km?.toLocaleString?.("pt-BR") ?? (a.start_km ?? "-")}</td>
                <td className="mono">{a.end_km?.toLocaleString?.("pt-BR") ?? (a.end_km ?? "-")}</td>
                <td><StatusBadge status={a.status} /></td>
                <td>
                  <button className="btn btn-ghost" onClick={() => printVehicleReceipt(a)} title="Imprimir recibo" data-testid={`vehicle-receipt-${a.id}`}>
                    <Printer size={13} />
                  </button>
                </td>
              </tr>
            ))}
            {assignments.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-sm" style={{ color: "var(--text-mute)" }}>Nenhuma atribuição.</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Editar Veículo" : "Novo Veículo"} testid="vehicle-modal"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setOpen(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={save} data-testid="save-vehicle-btn">Salvar</button>
        </>}>
        {error && <div className="mb-3 p-2 text-sm border" style={{ background: "rgba(239,68,68,0.08)", borderColor: "rgba(239,68,68,0.3)", color: "#FCA5A5" }}>{error}</div>}
        <div className="grid grid-cols-2 gap-3">
          <div><label className="label-caps block mb-1">Placa</label><input className="input mono" value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value.toUpperCase() })} data-testid="vehicle-plate" /></div>
          <div><label className="label-caps block mb-1">Status</label>
            <select className="select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="vehicle-status">
              <option value="ativo">Ativo</option><option value="manutencao">Manutenção</option><option value="inativo">Inativo</option>
            </select>
          </div>
          <div><label className="label-caps block mb-1">Modelo</label><input className="input" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} data-testid="vehicle-model" /></div>
          <div><label className="label-caps block mb-1">Marca</label><input className="input" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} data-testid="vehicle-brand" /></div>
          <div className="col-span-2"><label className="label-caps block mb-1">KM Atual</label><input className="input mono" type="number" value={form.km} onChange={(e) => setForm({ ...form, km: e.target.value })} data-testid="vehicle-km" /></div>
        </div>
      </Modal>

      <Modal open={aOpen} onClose={() => setAOpen(false)} title="Atribuir Veículo" testid="assignment-modal"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setAOpen(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={saveAssignment} data-testid="save-assignment-btn">Atribuir</button>
        </>}>
        {aError && <div className="mb-3 p-2 text-sm border" style={{ background: "rgba(239,68,68,0.08)", borderColor: "rgba(239,68,68,0.3)", color: "#FCA5A5" }}>{aError}</div>}
        <div className="grid gap-3">
          <div><label className="label-caps block mb-1">Veículo</label>
            <select className="select" value={aForm.vehicle_id} onChange={(e) => setAForm({ ...aForm, vehicle_id: e.target.value })} data-testid="assign-vehicle">
              <option value="">Selecione</option>
              {vehicles.map((v) => <option key={v.id} value={v.id}>{v.plate} — {v.model}</option>)}
            </select>
          </div>
          <div><label className="label-caps block mb-1">Funcionário</label>
            <select className="select" value={aForm.user_id} onChange={(e) => setAForm({ ...aForm, user_id: e.target.value })} data-testid="assign-user">
              <option value="">Selecione</option>
              {techs.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
            </select>
          </div>
          <div><label className="label-caps block mb-1">Data Início</label>
            <input className="input" type="date" value={aForm.start_date} onChange={(e) => setAForm({ ...aForm, start_date: e.target.value })} data-testid="assign-start" />
          </div>
          <div><label className="label-caps block mb-1">KM Inicial (opcional)</label>
            <input className="input mono" type="number" min="0" value={aForm.start_km} onChange={(e) => setAForm({ ...aForm, start_km: e.target.value })} data-testid="assign-start-km" placeholder="Deixe vazio para usar KM atual" />
          </div>
          <div className="pt-2">
            <SignaturePad
              value={aForm.signature}
              onChange={(sig) => setAForm({ ...aForm, signature: sig })}
              testid="vehicle-signature"
            />
            <div className="mt-4">
              <PhotoUploader
                value={aForm.photos}
                onChange={(photos) => setAForm({ ...aForm, photos })}
                testid="vehicle-photos"
              />
            </div>
          </div>
        </div>
      </Modal>

      <Modal open={closeOpen} onClose={() => setCloseOpen(false)} title="Encerrar Atribuição do Veículo" testid="close-assignment-modal"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setCloseOpen(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={doClose} data-testid="confirm-close-assignment">Encerrar</button>
        </>}>
        {closeErr && <div className="mb-3 p-2 text-sm border" style={{ background: "rgba(239,68,68,0.08)", borderColor: "rgba(239,68,68,0.3)", color: "#FCA5A5" }}>{closeErr}</div>}
        {closeForm.vehicle && (
          <>
            <div className="mb-3 p-3 border" style={{ borderColor: "var(--border-strong)" }}>
              <div className="label-caps mb-1">Veículo</div>
              <div className="mono font-semibold">{closeForm.vehicle.plate} · {closeForm.vehicle.model}</div>
              <div className="text-xs mt-1" style={{ color: "var(--text-mute)" }}>Com {closeForm.vehicle.current_holder_name} desde {closeForm.vehicle.current_since || "-"}</div>
              <div className="text-xs" style={{ color: "var(--text-mute)" }}>KM registrado: <b className="mono" style={{ color: "var(--text)" }}>{closeForm.vehicle.km?.toLocaleString?.("pt-BR")}</b></div>
            </div>
            <label className="label-caps block mb-1">KM Final (atualiza o veículo)</label>
            <input className="input mono mb-4" type="number" min={closeForm.vehicle.km || 0} value={closeForm.end_km} onChange={(e) => setCloseForm({ ...closeForm, end_km: e.target.value })} data-testid="close-end-km" />
            <label className="label-caps block mb-1">Observações da devolução</label>
            <input className="input mb-4" value={closeForm.notes} onChange={(e) => setCloseForm({ ...closeForm, notes: e.target.value })} placeholder="Estado do veículo, ocorrências..." data-testid="close-notes" />
            <SignaturePad
              value={closeForm.signature}
              onChange={(sig) => setCloseForm({ ...closeForm, signature: sig })}
              testid="close-signature"
            />
            <div className="mt-4">
              <PhotoUploader
                value={closeForm.photos}
                onChange={(photos) => setCloseForm({ ...closeForm, photos })}
                testid="close-photos"
              />
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
